import type { MatterContents, MatterFile, Proceeding, ProceedingRole } from "../contract"
import { lakh, shortDate } from "../answer/format"
import { isProcessing } from "./fileStatus"

/**
 * A proceeding is one anchor document (a notice, or an order with no notice here) and the documents
 * that belong to it. The backend groups them (proceedings.members); an older backend sends none, and
 * the groups are then worked out here from each file's kind.
 */

export type SessionKind = "oio" | "oia" | "notice" | "appeal"

/** The reader's word for what a file is in its proceeding. */
export function roleLabel(role: ProceedingRole | null | undefined, laterReply = false): string {
  switch (role) {
    case "scn": return "Notice"
    case "reply": return laterReply ? "Additional submission" : "Reply"
    case "hearing": return "Hearing record"
    case "order": return "Order"
    case "appeal": return "Appeal"
    default: return "Evidence"
  }
}

/** The role a file's kind (Nira's vocabulary) stands for, when the backend has not set one. */
const roleOfKind = (kind: string): ProceedingRole =>
  kind === "notice" ? "scn" : kind === "reply" ? "reply" : kind === "hearing" ? "hearing" : kind === "order" ? "order" : "other"

export const roleOf = (f: MatterFile): ProceedingRole => f.role ?? roleOfKind(f.kind)

export interface Member { file: MatterFile; role: ProceedingRole; label: string; date: string }

export interface Group {
  proceeding: Proceeding
  members: Member[]
}

/** The notice or order number without the document word the number often starts with ("SCN/04/2024-25" -> "04/2024-25"). */
const bareNumber = (n: string, kind: "scn" | "order"): string =>
  kind === "scn" ? n.replace(/^\s*(scn|show[\s-]*cause(\s+notice)?)[\s/.:-]*/i, "").trim() || n : n

/** "SCN 04/2024-25 · 16 Sep 2025", built from the number and date; the backend's own title only when it has neither. */
const titleOf = (p: Proceeding): string => {
  if (!p.number && !p.date) return p.title || (p.kind === "scn" ? "Notice" : "Order")
  const head = p.kind === "scn" ? "SCN" : "Order"
  return [p.number ? `${head} ${bareNumber(p.number, p.kind)}` : head, p.date ? shortDate(p.date) : ""].filter(Boolean).join(" · ")
}

const byDate = (a: Member, b: Member) => a.date.localeCompare(b.date)

export interface Shelf {
  groups: Group[]
  /** Files with a placing role (reply, hearing, order, appeal) that no proceeding holds. */
  unlinked: MatterFile[]
  /** Invoices, returns, contracts: workspace-wide. */
  evidence: MatterFile[]
  /** Files still being read: their role is not known yet. */
  reading: MatterFile[]
  /** Files that could not be read; shown so they can be deleted, never offered to a session. */
  failed: MatterFile[]
  /** True when the backend sent proceedings; false: they were worked out from file kinds. */
  fromBackend: boolean
}

/** Every file of a workspace placed: in a proceeding, unlinked, evidence, or still being read. */
export function shelfOf(contents: Pick<MatterContents, "files" | "proceedings" | "unlinked"> | null | undefined): Shelf {
  const all = contents?.files ?? []
  const failed = all.filter((f) => f.status === "failed")
  const files = all.filter((f) => f.status !== "failed")
  const byId = new Map(files.map((f) => [f.doc_id, f]))
  const reading = files.filter(isProcessing)
  const readingIds = new Set(reading.map((f) => f.doc_id))
  let proceedings = contents?.proceedings
  const fromBackend = proceedings !== undefined
  let unlinkedIds = new Set(contents?.unlinked ?? [])
  if (!proceedings) {
    // An older backend: each notice (or order, when there is no notice) anchors a proceeding; with a
    // single anchor every reply and hearing record joins it; otherwise they cannot be placed.
    const ready = files.filter((f) => !readingIds.has(f.doc_id))
    const notices = ready.filter((f) => f.kind === "notice")
    const anchors = notices.length ? notices : ready.filter((f) => f.kind === "order")
    const anchorKind = notices.length ? "scn" : "order"
    const placing = ready.filter((f) => !anchors.includes(f) && ["reply", "hearing", "order"].includes(f.kind))
    proceedings = anchors.map((a) => ({
      id: a.doc_id, kind: anchorKind, number: a.ref?.number ?? undefined, date: a.ref?.date ?? a.date, title: "",
      members: [
        { doc_id: a.doc_id, role: anchorKind === "scn" ? "scn" as const : "order" as const, date: a.date, linked_by: "anchor" as const },
        ...(anchors.length === 1 ? placing.map((f) => ({ doc_id: f.doc_id, role: roleOf(f), date: f.date, linked_by: "only" as const })) : []),
      ],
    }))
    unlinkedIds = new Set(anchors.length === 1 ? [] : placing.map((f) => f.doc_id))
  }
  const placed = new Set<string>()
  const groups: Group[] = proceedings.map((p) => {
    const raw: Member[] = []
    for (const m of p.members) {
      const file = byId.get(m.doc_id)
      if (!file || readingIds.has(m.doc_id)) continue
      placed.add(m.doc_id)
      raw.push({ file, role: m.role, label: "", date: m.date ?? file.date ?? "" })
    }
    raw.sort(byDate)
    let replies = 0
    for (const m of raw) m.label = roleLabel(m.role, m.role === "reply" && replies++ > 0)
    return { proceeding: { ...p, title: titleOf(p) }, members: raw }
  })
  const rest = files.filter((f) => !placed.has(f.doc_id) && !readingIds.has(f.doc_id))
  // A file the backend listed as unlinked, or (older backend) one with a placing role and no group.
  const unlinked = rest.filter((f) => unlinkedIds.has(f.doc_id))
  const evidence = rest.filter((f) => !unlinked.includes(f))
  return { groups, unlinked, evidence, reading, failed, fromBackend }
}

/** "2 replies, 1 hearing record" for a picker row. */
export function composition(g: Group): string {
  const n = (r: ProceedingRole) => g.members.filter((m) => m.role === r).length
  const part = (count: number, one: string, many: string) => (count ? `${count} ${count === 1 ? one : many}` : "")
  return [part(n("reply"), "reply", "replies"), part(n("hearing"), "hearing record", "hearing records"), part(n("order"), "order", "orders"), part(n("appeal"), "appeal", "appeals")]
    .filter(Boolean).join(", ")
}

/** "Rs 12.4 lakh demanded" or "". */
export const demandLine = (p: Pick<Proceeding, "demand_total">): string =>
  typeof p.demand_total === "number" && p.demand_total > 0 ? `${lakh(p.demand_total)} demanded` : ""

/** The groups a kind of session can start from: notices for OIO and Notice analysis, orders for OIA and Appeal or not. */
export function optionsFor(shelf: Shelf, kind: SessionKind): Group[] {
  const wantScn = kind === "oio" || kind === "notice"
  const out = shelf.groups.filter((g) => (wantScn ? g.proceeding.kind === "scn" : g.members.some((m) => m.role === "order")))
  return out.sort((a, b) => (b.proceeding.date ?? "").localeCompare(a.proceeding.date ?? ""))
}

/** The doc a Notice analysis or an Appeal or not session reads: the notice itself, or the order. */
export function anchorDoc(g: Group, kind: SessionKind): string {
  if (kind === "appeal" || kind === "oia") return g.members.find((m) => m.role === "order")?.file.doc_id ?? g.proceeding.id
  return g.proceeding.id
}

const LONG: Record<ProceedingRole, string> = {
  scn: "Show cause notice", reply: "Reply", hearing: "Hearing record", order: "Order", appeal: "Appeal", other: "",
}

/** A file name made readable: no extension, dots and underscores as spaces. */
export const cleanName = (name: string): string =>
  name.replace(/\.[A-Za-z0-9]{2,5}$/, "").replace(/[._]+/g, " ").replace(/\s+/g, " ").trim() || name

/**
 * What a document is called in a list: its role, its own number and its date ("Show cause notice
 * 04/2025-26 · 16 Sep 2025", "Reply · 16 Oct 2025"); the cleaned file name when the role is not known.
 */
export function docName(f: MatterFile, role: ProceedingRole | null | undefined = roleOf(f), laterReply = false): string {
  if (!role || role === "other") return cleanName(f.name)
  const head = role === "reply" && laterReply ? "Additional submission" : LONG[role]
  const date = f.ref?.date ?? f.date
  if (!f.ref?.number && !date) return cleanName(f.name)
  return [f.ref?.number ? `${head} ${role === "scn" ? bareNumber(f.ref.number, "scn") : f.ref.number}` : head, date ? shortDate(date) : ""].filter(Boolean).join(" · ")
}

/** "SCN 04/2024-25" or "Order 123/2025": the proceeding's short name, without its date. */
export const shortTitle = (p: Pick<Proceeding, "kind" | "number" | "title">): string => {
  if (!p.number) return p.kind === "scn" ? "Notice" : "Order"
  return `${p.kind === "scn" ? "SCN" : "Order"} ${bareNumber(p.number, p.kind)}`
}

/** "Show cause notice 04/2024-25": a proceeding's name as a section heading. */
export const longTitle = (p: Pick<Proceeding, "kind" | "number" | "title">): string => {
  if (!p.number) return p.kind === "scn" ? "Show cause notice" : "Order"
  return `${p.kind === "scn" ? "Show cause notice" : "Order"} ${bareNumber(p.number, p.kind)}`
}

/**
 * Which rows (sessions, readings) belong to which proceeding: by the documents they were on, and only
 * when that says nothing by an "SCN <number>" in the title. A row is placed once, on its best match.
 */
export function placeRows<R extends { key: string; title: string; docIds?: string[] }>(rows: R[], groups: Group[]): { by: Map<string, R[]>; placed: Set<string> } {
  const by = new Map<string, R[]>(groups.map((g) => [g.proceeding.id, []]))
  const placed = new Set<string>()
  for (const r of rows) {
    let best = ""
    let score = 0
    if (r.docIds?.length) {
      for (const g of groups) {
        const n = g.members.filter((m) => r.docIds!.includes(m.file.doc_id)).length
        if (n > score) { score = n; best = g.proceeding.id }
      }
    } else {
      const t = r.title.toLowerCase()
      for (const g of groups) {
        const num = g.proceeding.number ? bareNumber(g.proceeding.number, g.proceeding.kind).toLowerCase() : ""
        if (num.length >= 4 && t.includes(num)) { best = g.proceeding.id; break }
      }
    }
    if (best) { by.get(best)!.push(r); placed.add(r.key) }
  }
  return { by, placed }
}
